import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, API } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Building2, Loader2, Eye, Pencil, Wallet, Plus, Trash2, Search, CheckCircle2, AlertTriangle, IndianRupee, CalendarClock, Download } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  Paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
  Partial: "bg-amber-100 text-amber-700 border-amber-200",
  Unpaid: "bg-slate-100 text-slate-600 border-slate-200",
  Expired: "bg-rose-100 text-rose-700 border-rose-200",
  Disabled: "bg-rose-50 text-rose-500 border-rose-200",
  "No Plan": "bg-slate-100 text-slate-500 border-slate-200",
};
const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque", "Card", "Online"];
const Badge = ({ s }) => <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLOR[s] || STATUS_COLOR["No Plan"]}`} data-testid="school-status-badge">{s}</span>;

export default function Schools() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [plans, setPlans] = useState([]);
  const [search, setSearch] = useState("");
  const [editSchool, setEditSchool] = useState(null);
  const [paySchool, setPaySchool] = useState(null);
  const [viewSchool, setViewSchool] = useState(null);

  const load = () => api.get("/schools").then((r) => setRows(r.data)).catch((e) => { toast.error(apiErr(e.response?.data?.detail)); nav("/dashboard"); });
  useEffect(() => { load(); api.get("/subscription-plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  if (!rows) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-indigo-500" /></div>;

  const q = search.toLowerCase();
  const list = rows.filter((r) => !q || [r.school_name, r.username, r.plan].some((v) => (v || "").toLowerCase().includes(q)));
  const totalCollected = rows.reduce((a, r) => a + (r.amount_paid || 0), 0);
  const active = rows.filter((r) => r.payment_status === "Paid" || r.payment_status === "Partial").length;
  const expired = rows.filter((r) => r.payment_status === "Expired").length;

  return (
    <div className="space-y-6" data-testid="schools-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900 flex items-center gap-2"><Building2 className="text-indigo-600" />Schools</h1>
          <p className="text-slate-500 text-sm mt-1">Manage each school's plan, subscription dates, payment status and payment history.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Total Schools" value={rows.length} icon={Building2} tone="bg-indigo-100 text-indigo-600" />
        <Stat label="Active Plans" value={active} icon={CheckCircle2} tone="bg-emerald-100 text-emerald-600" />
        <Stat label="Expired" value={expired} icon={AlertTriangle} tone="bg-rose-100 text-rose-600" />
        <Stat label="Total Collected" value={fmt(totalCollected)} icon={IndianRupee} tone="bg-violet-100 text-violet-600" />
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input className="pl-9" placeholder="Search by school, username or plan…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="schools-search" />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="schools-table">
        <table className="w-full text-sm min-w-[1000px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              {["School Name", "Plan", "Start Date", "End Date", "Payment Status", "Amount Paid", "Next Renewal", "Actions"].map((h) => <th key={h} className={`px-4 py-3 ${h === "Actions" ? "text-right" : "text-left"} whitespace-nowrap`}>{h}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {list.length === 0 ? <tr><td colSpan={8} className="py-14 text-center text-slate-400">No schools found.</td></tr> :
              list.map((r) => (
                <tr key={r.admin_id} className="hover:bg-slate-50" data-testid={`school-row-${r.username}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{r.school_name}</div>
                    <div className="text-xs text-slate-400 font-mono">{r.username}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{r.plan || <span className="text-slate-300">—</span>}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.start_date ? fmtDate(r.start_date) : "—"}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.end_date ? fmtDate(r.end_date) : "—"}</td>
                  <td className="px-4 py-3"><Badge s={r.payment_status} /></td>
                  <td className="px-4 py-3 font-semibold text-slate-800 tabular">{fmt(r.amount_paid)}<span className="text-xs text-slate-400 font-normal"> / {fmt(r.net_amount)}</span></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{r.next_renewal_date ? fmtDate(r.next_renewal_date) : "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" className="gap-1 text-slate-600" onClick={() => setViewSchool(r)} data-testid={`school-view-${r.username}`}><Eye size={14} />View</Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-indigo-600" onClick={() => setEditSchool(r)} data-testid={`school-edit-plan-${r.username}`}><Pencil size={14} />Plan</Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-emerald-600" onClick={() => setPaySchool(r)} data-testid={`school-payments-${r.username}`}><Wallet size={14} />Payments</Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {editSchool && <EditPlanDialog school={editSchool} plans={plans} onClose={() => setEditSchool(null)} onSaved={() => { setEditSchool(null); load(); }} />}
      {paySchool && <PaymentsDialog school={paySchool} onClose={() => setPaySchool(null)} onChanged={load} />}
      {viewSchool && <DetailsDialog school={viewSchool} onClose={() => setViewSchool(null)} />}
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`schools-stat-${label}`}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tone}`}><Icon size={20} /></div>
    <div><div className="text-2xl font-heading font-bold text-slate-900 leading-none">{value}</div><div className="text-xs text-slate-400 uppercase tracking-wide mt-1">{label}</div></div>
  </div>
);

function EditPlanDialog({ school, plans, onClose, onSaved }) {
  const [f, setF] = useState({ plan: school.plan || "", plan_amount: school.plan_amount || 0, discount: school.discount || 0, start_date: school.start_date || "", end_date: school.end_date || "", sub_enabled: school.sub_enabled !== false, auto_disable_on_expiry: school.auto_disable_on_expiry !== false });
  const [saving, setSaving] = useState(false);
  const pickPlan = (name) => { const p = plans.find((x) => x.name === name); setF((s) => ({ ...s, plan: name, plan_amount: p ? p.amount : s.plan_amount })); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/schools/${school.admin_id}/plan`, f); toast.success("Plan updated"); onSaved(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="edit-plan-dialog">
        <DialogHeader><DialogTitle>Edit Plan — {school.school_name}</DialogTitle><DialogDescription>Assign a subscription plan and set the validity period.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Plan</Label>
            <Select value={f.plan || "none"} onValueChange={(v) => pickPlan(v === "none" ? "" : v)}><SelectTrigger data-testid="plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
              <SelectContent><SelectItem value="none">No plan</SelectItem>{plans.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} — ₹{p.amount}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Plan Amount (₹)</Label><Input type="number" value={f.plan_amount} onChange={(e) => setF({ ...f, plan_amount: e.target.value })} data-testid="plan-amount-input" /></div>
            <div><Label className="text-xs">Discount (₹)</Label><Input type="number" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} data-testid="plan-discount-input" /></div>
            <div><Label className="text-xs">Start Date</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="plan-start-input" /></div>
            <div><Label className="text-xs">End Date (Renewal)</Label><Input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="plan-end-input" /></div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div><div className="text-sm font-medium text-slate-700">Subscription Active</div><div className="text-xs text-slate-400">Disabling blocks this school's login.</div></div>
            <Switch checked={f.sub_enabled} onCheckedChange={(v) => setF({ ...f, sub_enabled: v })} data-testid="plan-enabled-switch" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
            <div><div className="text-sm font-medium text-slate-700">Auto-disable on expiry</div><div className="text-xs text-slate-400">Block login automatically once the End Date passes.</div></div>
            <Switch checked={f.auto_disable_on_expiry} onCheckedChange={(v) => setF({ ...f, auto_disable_on_expiry: v })} data-testid="plan-autodisable-switch" />
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={saving} data-testid="save-plan-btn">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Plan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function usePaymentDetail(adminId) {
  const [detail, setDetail] = useState(null);
  const reload = () => api.get(`/schools/${adminId}`).then((r) => setDetail(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [adminId]);
  return [detail, reload];
}

function PaymentsDialog({ school, onClose, onChanged }) {
  const [detail, reload] = usePaymentDetail(school.admin_id);
  const [f, setF] = useState({ amount: "", date: new Date().toISOString().slice(0, 10), mode: "Cash", note: "" });
  const [saving, setSaving] = useState(false);
  const add = async () => {
    if (!Number(f.amount)) { toast.error("Enter a payment amount"); return; }
    setSaving(true);
    try { await api.post(`/schools/${school.admin_id}/payments`, { ...f, amount: Number(f.amount) }); toast.success("Payment recorded"); setF({ amount: "", date: new Date().toISOString().slice(0, 10), mode: "Cash", note: "" }); reload(); onChanged(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const del = async (pid) => { try { await api.delete(`/schools/${school.admin_id}/payments/${pid}`); toast.success("Payment removed"); reload(); onChanged(); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="payments-dialog">
        <DialogHeader><DialogTitle>Manage Payments — {school.school_name}</DialogTitle><DialogDescription>Record a payment and review the full payment history.</DialogDescription></DialogHeader>
        {detail && (
          <div className="grid grid-cols-3 gap-2 text-center text-sm mb-1">
            <div className="rounded-lg bg-slate-50 py-2"><div className="text-xs text-slate-400">Net Amount</div><div className="font-bold text-slate-800">{fmt(detail.net_amount)}</div></div>
            <div className="rounded-lg bg-emerald-50 py-2"><div className="text-xs text-emerald-500">Paid</div><div className="font-bold text-emerald-700">{fmt(detail.amount_paid)}</div></div>
            <div className="rounded-lg bg-rose-50 py-2"><div className="text-xs text-rose-500">Balance</div><div className="font-bold text-rose-700">{fmt(detail.balance)}</div></div>
          </div>
        )}
        <div className="rounded-lg border border-slate-200 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
          <div><Label className="text-xs">Amount (₹)</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="payment-amount-input" /></div>
          <div><Label className="text-xs">Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="payment-date-input" /></div>
          <div><Label className="text-xs">Mode</Label>
            <Select value={f.mode} onValueChange={(v) => setF({ ...f, mode: v })}><SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger><SelectContent>{MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
          </div>
          <Button onClick={add} disabled={saving} className="gap-1.5" data-testid="add-payment-btn">{saving ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />}Add</Button>
          <div className="col-span-2 sm:col-span-4"><Label className="text-xs">Note (optional)</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="e.g. Q1 renewal" data-testid="payment-note-input" /></div>
        </div>
        <PaymentHistory payments={detail?.payments} onDelete={del} adminId={school.admin_id} />
      </DialogContent>
    </Dialog>
  );
}

function PaymentHistory({ payments, onDelete, adminId }) {
  if (!payments) return <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-indigo-400" size={18} /></div>;
  const receiptUrl = (pid) => `${API}/schools/${adminId}/payments/${pid}/receipt?token=${localStorage.getItem("token")}`;
  return (
    <div className="mt-2" data-testid="payment-history">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Payment History</div>
      {payments.length === 0 ? <div className="py-8 text-center text-slate-400 text-sm">No payments recorded yet.</div> : (
        <table className="w-full text-sm border border-slate-200">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>{["Date", "Amount", "Mode", "Note", "By", ""].map((h) => <th key={h} className="px-3 py-2 text-left border border-slate-200">{h}</th>)}</tr></thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} data-testid={`payment-row-${p.id}`}>
                <td className="px-3 py-2 border border-slate-200 whitespace-nowrap">{fmtDate(p.date)}</td>
                <td className="px-3 py-2 border border-slate-200 font-semibold text-emerald-700">{fmt(p.amount)}</td>
                <td className="px-3 py-2 border border-slate-200">{p.mode}</td>
                <td className="px-3 py-2 border border-slate-200 text-slate-500">{p.note || "—"}</td>
                <td className="px-3 py-2 border border-slate-200 text-slate-400 text-xs">{p.created_by || "—"}</td>
                <td className="px-3 py-2 border border-slate-200 text-right whitespace-nowrap">
                  {adminId && <a href={receiptUrl(p.id)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800 inline-flex mr-2 align-middle" title="Download receipt" data-testid={`receipt-payment-${p.id}`}><Download size={14} /></a>}
                  {onDelete && <button className="text-rose-500 hover:text-rose-700 align-middle" onClick={() => onDelete(p.id)} data-testid={`delete-payment-${p.id}`}><Trash2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DetailsDialog({ school, onClose }) {
  const [detail] = usePaymentDetail(school.admin_id);
  const Row = ({ k, v }) => <div className="flex justify-between py-1.5 border-b border-slate-100 text-sm"><span className="text-slate-400">{k}</span><span className="text-slate-800 font-medium text-right">{v || "—"}</span></div>;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="details-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Building2 size={18} className="text-indigo-600" />{school.school_name}</DialogTitle><DialogDescription>Full subscription details and payment history.</DialogDescription></DialogHeader>
        {!detail ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div> : (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-x-6">
              <Row k="Username" v={detail.username} />
              <Row k="Account Status" v={detail.status} />
              <Row k="Plan" v={detail.plan} />
              <Row k="Payment Status" v={<Badge s={detail.payment_status} />} />
              <Row k="Plan Amount" v={fmt(detail.plan_amount)} />
              <Row k="Discount" v={fmt(detail.discount)} />
              <Row k="Net Amount" v={fmt(detail.net_amount)} />
              <Row k="Amount Paid" v={fmt(detail.amount_paid)} />
              <Row k="Balance" v={fmt(detail.balance)} />
              <Row k="Subscription" v={detail.sub_enabled ? "Active" : "Disabled"} />
              <Row k="Start Date" v={detail.start_date ? fmtDate(detail.start_date) : "—"} />
              <Row k="End / Renewal Date" v={detail.end_date ? fmtDate(detail.end_date) : "—"} />
              <Row k="Students" v={detail.student_count} />
              <Row k="Staff" v={detail.staff_count} />
              <Row k="Created On" v={detail.created_at ? fmtDate(detail.created_at) : "—"} />
            </div>
            <PaymentHistory payments={detail.payments} />
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
